<template>
	<Dialog
		:dialog-visible="dialogVisible"
		title="Share Sheet"
		:remove-footer="true"
		:options="{ width: '520px' }"
		@cancel="emit('cancel')">
		<template #dialog-header>
			<v-btn
				icon="icon-close-medium"
				variant="text"
				density="comfortable"
				class="close-btn"
				aria-label="Close"
				@click="emit('cancel')" />
		</template>

		<template #dialog-body>
			<div class="d-flex flex-column ga-4">
				<!-- Copy link -->
				<div>
					<div class="text-body-2 font-weight-bold mb-2">Sheet Link</div>
					<div class="d-flex ga-2">
						<v-text-field
							ref="linkField"
							:model-value="displayUrl"
							readonly
							density="compact"
							hide-details
							:loading="shorteningUrl"
							@focus="($event.target as HTMLInputElement)?.select()" />
						<PrimaryBtn :min-width="100" :disabled="shorteningUrl" @click="copyLink">
							{{ copied ? "Copied!" : "Copy" }}
						</PrimaryBtn>
					</div>
				</div>

				<v-divider />

				<!-- Share via text (SMS) -->
				<div>
					<div class="d-flex align-center ga-2 mb-2">
						<v-icon size="18" color="primary">mdi-message-text-outline</v-icon>
						<div class="text-body-2 font-weight-bold">Share via Text</div>
					</div>
					<RecipientPicker
						ref="smsPickerRef"
						v-model="smsRecipients"
						channel="phone"
						label="Contacts or phone numbers"
						placeholder="Pick a contact or type a phone number"
						helper-text="Type to search contacts or enter a phone number."
						:customers="allCustomers"
						:loading="loadingCustomers"
						:max-recipients="MAX_RECIPIENTS_PER_CHANNEL"
						:default-country="currentOrganizationDefaultCountry" />
				</div>

				<!-- Share via email -->
				<div>
					<div class="d-flex align-center ga-2 mb-2">
						<v-icon size="18" color="primary">mdi-email-outline</v-icon>
						<div class="text-body-2 font-weight-bold">Share via Email</div>
					</div>
					<RecipientPicker
						ref="emailPickerRef"
						v-model="emailRecipients"
						channel="email"
						label="Contacts or email addresses"
						placeholder="Pick a contact or type an email"
						helper-text="Type to search contacts or enter an email address."
						:customers="allCustomers"
						:loading="loadingCustomers"
						:max-recipients="MAX_RECIPIENTS_PER_CHANNEL" />
				</div>

				<v-divider />

				<div class="d-flex flex-column ga-2">
					<PrimaryBtn :loading="sending" @click="sendShare">
						Send
					</PrimaryBtn>
					<SecondaryBtn prepend-icon="mdi-eye" @click="previewSheet">
						Preview Sheet
					</SecondaryBtn>
				</div>
			</div>
		</template>
	</Dialog>
</template>

<script lang="ts" setup>
	import { ref, computed, watch, nextTick } from "vue";
	import { useToast } from "vue-toastification";
	import Dialog from "@/components/Dialog.vue";
	import RecipientPicker from "@/components/RecipientPicker.vue";
	import { copyToClipboard, calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { logError } from "@/helpers/logError";
	import type { ContactRecipient } from "@/composables/useContactRecipientSearch";
	import { hasInvalidRecipients } from "@/composables/useReportShare";
	import { useOrganization } from "@/composables/useOrganization";
	import { useLatestRequest } from "@/composables/useLatestRequest";
	import Dialogs from "@/helpers/dialogs";
	import api from "@/api";

	interface Props {
		dialogVisible: boolean;
		sheetId: string;
		sheetUrl: string;
		sheetName?: string;
	}

	const props = defineProps<Props>();
	const emit = defineEmits<{
		(event: "cancel"): void;
	}>();

	const toast = useToast();

	const MAX_RECIPIENTS_PER_CHANNEL = 10;

	const copied = ref(false);
	const sending = ref(false);
	const allCustomers = ref<unknown[]>([]);
	const loadingCustomers = ref(false);
	const emailRecipients = ref<ContactRecipient[]>([]);
	const smsRecipients = ref<ContactRecipient[]>([]);
	const shortUrl = ref<string | null>(null);
	const shorteningUrl = ref(false);
	const shareLoadRequest = useLatestRequest();

	// Refs to the two pickers so Send can flush any typed-but-not-Entered input before reading
	// recipients (LSP-16630). Only the flush method is needed from each picker's exposed surface.
	type RecipientPickerExposed = { flushPendingInput: () => void };
	const smsPickerRef = ref<RecipientPickerExposed | null>(null);
	const emailPickerRef = ref<RecipientPickerExposed | null>(null);

	const { currentOrganizationDefaultCountry } = useOrganization();

	// Prefer the short URL when one has been generated; fall back to the long URL so users
	// always see *something* copyable if the shortener is slow or fails.
	const displayUrl = computed(() => shortUrl.value ?? props.sheetUrl);

	watch(
		[() => props.dialogVisible, () => props.sheetId, () => props.sheetUrl],
		async ([visible]) => {
			if (!visible) {
				shareLoadRequest.abortCurrent();
				loadingCustomers.value = false;
				shorteningUrl.value = false;
				return;
			}

			await loadShareData();
		},
		{ immediate: true }
	);

	// Load all contacts once when the dialog opens and share them between both pickers. Local
	// filtering avoids keystroke/response race conditions entirely — every keystroke sees an
	// up-to-date, fully-consistent result set. In parallel, request a short URL for sharing.
	async function loadShareData() {
		const { requestId } = shareLoadRequest.startRequest();
		const sheetId = props.sheetId;
		const sheetUrl = props.sheetUrl;

		emailRecipients.value = [];
		smsRecipients.value = [];
		allCustomers.value = [];
		shortUrl.value = null;
		loadingCustomers.value = true;
		shorteningUrl.value = true;

		const contactsTask = (async () => {
			try {
				// 9999: orgs with >1k customers would otherwise see silent truncation in the pickers.
				// The rest of the app keeps the 1000 default for its own dialogs.
				const customers = (await Dialogs.getCustomers("", 9999)) ?? [];
				if (!shareLoadRequest.isCurrent(requestId)) return;
				allCustomers.value = customers;
			} catch (err) {
				if (!shareLoadRequest.isCurrent(requestId)) return;
				logError("ShareSheetDialog: failed to load contacts", err, { sheetId });
				allCustomers.value = [];
			} finally {
				// Release the spinner unless a newer request superseded this one (LSP-15911).
				if (!shareLoadRequest.isSuperseded(requestId)) {
					loadingCustomers.value = false;
				}
			}
		})();

		const shortenTask = (async () => {
			try {
				const result = await api.createShortenedUrl(sheetUrl);
				if (!shareLoadRequest.isCurrent(requestId)) return;
				if (result?.url) shortUrl.value = result.url;
			} catch (err) {
				if (!shareLoadRequest.isCurrent(requestId)) return;
				// Non-fatal — the dialog still shows the long URL.
				logError("ShareSheetDialog: failed to shorten sheet URL", err, { sheetId });
			} finally {
				// Release the spinner unless a newer request superseded this one (LSP-15911).
				if (!shareLoadRequest.isSuperseded(requestId)) {
					shorteningUrl.value = false;
				}
			}
		})();

		await Promise.allSettled([contactsTask, shortenTask]);
	}

	// RecipientPicker preserves invalid free-text as a red `valid:false` pill (LSP-16629) rather than
	// dropping it, so this dialog — like ShareReportDialog — must block sending invalid recipients to
	// api.shareSheet. Scan each list directly (no combined-array allocation); the email scan
	// short-circuits before touching SMS.
	const anyRecipientInvalid = computed(() => hasInvalidRecipients(emailRecipients.value) || hasInvalidRecipients(smsRecipients.value));
	const canSend = computed(() => (emailRecipients.value.length > 0 || smsRecipients.value.length > 0) && !anyRecipientInvalid.value);

	async function copyLink() {
		const success = await copyToClipboard(displayUrl.value);
		if (success) {
			copied.value = true;
			const message = "Sheet link copied to clipboard!";
			toast.success(message, { timeout: calculateTimeout(message) });
			setTimeout(() => {
				copied.value = false;
			}, 2000);
		} else {
			const message = "Failed to copy link to clipboard.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}

	async function sendShare() {
		if (sending.value) return;
		// Commit any typed-but-not-Entered values first so a pending recipient isn't dropped on Send
		// (LSP-16630). flushPendingInput is a no-op when the field is empty.
		smsPickerRef.value?.flushPendingInput();
		emailPickerRef.value?.flushPendingInput();
		await nextTick();

		// A just-flushed value may be invalid — block the send and let the per-picker red pills and
		// inline errors show the user what to fix (LSP-16629 / LSP-16630).
		if (anyRecipientInvalid.value) {
			const message = "Some recipients aren't valid yet — fix the highlighted ones and try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
			return;
		}
		if (!canSend.value) {
			const message = "Add a recipient — a contact, phone number or email address — to share this sheet.";
			toast.info(message, { timeout: calculateTimeout(message) });
			return;
		}
		sending.value = true;
		try {
			const result = await api.shareSheet(props.sheetId, {
				emails: emailRecipients.value.map((recipient) => recipient.value),
				phoneNumbers: smsRecipients.value.map((recipient) => recipient.value)
			});
			renderSendOutcome(result);
		} catch (err) {
			logError("ShareSheetDialog: share send failed", err, {
				sheetId: props.sheetId,
				emailCount: emailRecipients.value.length,
				phoneCount: smsRecipients.value.length
			});
			const message = getMessageFromError(err) || "Something went wrong sending the sheet. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		} finally {
			sending.value = false;
		}
	}

	function renderSendOutcome(result: { emailsSent: number; smsSent: number; failedEmails: string[]; failedPhoneNumbers: string[] }) {
		const totalSent = result.emailsSent + result.smsSent;
		const totalFailed = result.failedEmails.length + result.failedPhoneNumbers.length;

		if (totalSent > 0 && totalFailed === 0) {
			const parts: string[] = [];
			if (result.emailsSent > 0) parts.push(pluralize(result.emailsSent, "email"));
			if (result.smsSent > 0) parts.push(pluralize(result.smsSent, "text message"));
			const message = `Your sheet has been shared — we sent ${joinList(parts)}.`;
			toast.success(message, { timeout: calculateTimeout(message) });
			emailRecipients.value = [];
			smsRecipients.value = [];
			emit("cancel");
			return;
		}

		if (totalSent > 0 && totalFailed > 0) {
			const message = `Sent ${totalSent}, but couldn't reach ${totalFailed}. The failed recipients are still in the list — try sending again.`;
			toast.warning(message, { timeout: calculateTimeout(message) });
			const failedEmailSet = new Set(result.failedEmails);
			const failedPhoneSet = new Set(result.failedPhoneNumbers);
			emailRecipients.value = emailRecipients.value.filter((recipient) => failedEmailSet.has(recipient.value));
			smsRecipients.value = smsRecipients.value.filter((recipient) => failedPhoneSet.has(recipient.value));
			return;
		}

		const message = "We couldn't send this sheet to anyone. Double-check the addresses and try again.";
		toast.error(message, { timeout: calculateTimeout(message) });
	}

	function pluralize(count: number, word: string): string {
		return `${count} ${word}${count === 1 ? "" : "s"}`;
	}
	function joinList(items: string[]): string {
		if (items.length <= 1) return items[0] ?? "";
		return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
	}

	function previewSheet() {
		// Preview always uses the raw URL — the short URL is for external sharing, not internal nav.
		const win = window.open(props.sheetUrl, "_blank");
		if (!win) {
			// LSP-15319: Preview was a dead button when the popup was blocked or the browser had
			// hit its tab limit (iOS Safari caps around 500). The two are not programmatically
			// distinguishable and manifest identically, so one null-check covers both.
			console.warn("ShareSheetDialog.previewSheet: window.open returned null — popup blocked or tab limit reached", {
				hasSheetUrl: !!props.sheetUrl
			});
			const message = "Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}

	defineExpose({ sendShare, canSend });
</script>

<style scoped>
	.close-btn {
		position: absolute;
		top: 8px;
		right: 8px;
	}
</style>
